/**
 * Uploading a finished policy.
 *
 * Three steps, in this order, because each one needs the result of the last:
 *
 *   1. `savePolicyDraft` creates the record and assigns the policy number.
 *   2. The PDF goes straight to Cloud Storage from the browser. The rules
 *      permit an author to write to that path, and routing tens of megabytes
 *      through a Cloud Function to achieve the same thing would be slower and
 *      would cap the file size at the function's request limit.
 *   3. `addDraftAttachment` records the file against the draft.
 *
 * Step 2 is the only one that can take minutes, so it reports progress.
 *
 * PDF ONLY, deliberately. The storage rules would accept Word, Excel and
 * images too, but the product does not convert anything — a Word file would
 * upload happily and then fail to display, which is a worse experience than
 * being told up front. See docs/rewrite/scope.md.
 */

import { useCallback, useState } from 'react';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { useQueryClient } from '@tanstack/react-query';
import { app } from '../../lib/firebase';
import { call, isCallFailure } from '../../lib/callable';

const storage = getStorage(app);

/** Bytes as something a person reads. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The storage rules cap an attachment at 25 MB. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const ACCEPTED_TYPE = 'application/pdf';
export const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** What the file pickers offer. Word is accepted because hospitals write in it;
 *  the server converts it to the PDF staff read. */
export const ACCEPT = 'application/pdf,.pdf,' + DOCX_TYPE + ',.docx';

export type DocumentKind = 'pdf' | 'docx';

/**
 * PDF or Word, by type or by name. Windows and some browsers report an
 * empty type for .docx, so the extension counts too.
 */
export function documentKind(file: { type: string; name: string }): DocumentKind | null {
  if (file.type === ACCEPTED_TYPE || /\.pdf$/i.test(file.name)) return 'pdf';
  if (file.type === DOCX_TYPE || /\.docx$/i.test(file.name)) return 'docx';
  return null;
}

export interface UploadInput {
  title: string;
  summary?: string;
  categoryId?: string | null;
  documentTypeId?: string | null;
  departmentIds?: string[];
  reviewCycleMonths?: number;
  requiresAcknowledgement: boolean;
  /** Who is asked to acknowledge on publish. 'departments' needs departmentIds. */
  targetAudience?: 'all' | 'departments';
  file: File;
  /**
   * ONE ATTEMPT, ONE POLICY, HOWEVER OFTEN IT IS RETRIED.
   *
   * Uploading creates the policy record and spends its number BEFORE the file
   * is sent. A failed upload used to leave that record behind, and pressing
   * Retry made a second one; a bulk retry made one per file per attempt, and a
   * library filled with numbered ghosts nobody had asked for. Minted once per
   * file and sent again on every retry, so the server answers a repeat with
   * the policy it already created.
   */
  attemptId: string;
  /**
   * The file's contents, already read by probeFile. The upload sends THESE,
   * not the File — see probeFile for why the two are not the same thing.
   */
  bytes: Uint8Array;
  /** Send it straight into the approval workflow rather than leaving a draft. */
  submitForReview: boolean;
}

export type UploadStage =
  | { phase: 'idle' }
  | { phase: 'creating' }
  | { phase: 'uploading'; percent: number }
  | { phase: 'attaching' }
  /** A Word document is being turned into the PDF staff will read. */
  | { phase: 'converting' }
  | { phase: 'submitting' }
  /** `note` carries something that went wrong without undoing the upload. */
  | { phase: 'done'; policyId: string; note?: string }
  | { phase: 'failed'; message: string };

export const NOT_DOCUMENT_MESSAGE =
  'That is not a PDF or a Word document. Policies are uploaded as PDF or .docx.';

/** Why a file cannot be accepted, or null when it can. */
export function rejectReason(file: File | null): string | null {
  if (!file) return 'Choose the PDF or Word document to upload.';
  if (!documentKind(file)) return NOT_DOCUMENT_MESSAGE;
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `That file is ${mb} MB. The limit is 25 MB.`;
  }
  if (file.size === 0) return 'That file is empty.';
  return null;
}

// ============================================================================
// Reading the file before trusting it
// ============================================================================

/**
 * A File is a promise, not a payload.
 *
 * What the picker hands over is a HANDLE: a name, a size, a type, and the
 * ability to ask for the bytes later. Whether the bytes exist is a separate
 * question, and on a modern laptop the answer is often no. Google Drive,
 * iCloud and OneDrive all show files that are "available online" — the Finder
 * reports "361,242 bytes (Zero bytes on disk)" — and the browser receives a
 * File claiming 353 KB whose read then waits on the cloud client, which may
 * be signed out, offline, or simply slow. The old code handed that File to
 * Cloud Storage, which sat at "Uploading… 0%" for as long as anybody cared to
 * watch. That is the report this exists to answer.
 *
 * So the bytes are read HERE, up front, with a deadline, and the upload sends
 * the bytes rather than the File. If they can be read they can be sent; if
 * they cannot, the person is told what the file is and what to do about it,
 * before a draft policy has been created for a document that never arrives.
 *
 * Reading the whole file also lets us look inside it. `file.type` comes from
 * the extension, so a Word document renamed .pdf sails through the MIME check;
 * a real PDF starts with the bytes "%PDF". That is checked too.
 */

export interface Readable {
  size: number;
  arrayBuffer(): Promise<ArrayBuffer>;
}

export type ProbeResult =
  | { ok: true; bytes: Uint8Array; kind: DocumentKind }
  | { ok: false; reason: string };

export const UNREADABLE_MESSAGE =
  'This file could not be read from your computer. Files kept in Google Drive, ' +
  'iCloud or OneDrive are often only placeholders until opened — make it available ' +
  'offline (in Drive: right-click → Available offline), or download a copy, then ' +
  'choose it again.';

export const NOT_PDF_MESSAGE =
  'That file is not a PDF or a Word document inside, whatever its name says. Export it as a PDF or save it as .docx and try again.';

/**
 * How long to wait for the bytes.
 *
 * A file on the disk reads in milliseconds whatever its size. A cloud
 * placeholder that CAN hydrate needs time proportional to its size, and one
 * that cannot needs a deadline. Ten seconds plus two per megabyte, capped at a
 * minute, gives a 25 MB file that is genuinely being fetched a fair chance
 * without leaving anyone staring at "Checking…" indefinitely.
 */
export function readTimeoutFor(sizeBytes: number): number {
  const mb = sizeBytes / (1024 * 1024);
  return Math.min(60_000, 10_000 + Math.round(mb * 2_000));
}

/** "%PDF" somewhere in the first kilobyte, which is where the spec allows it. */
function looksLikePdf(bytes: Uint8Array): boolean {
  const head = bytes.subarray(0, 1024);
  for (let i = 0; i + 3 < head.length; i++) {
    if (head[i] === 0x25 && head[i + 1] === 0x50 && head[i + 2] === 0x44 && head[i + 3] === 0x46) {
      return true;
    }
  }
  return false;
}

/** A .docx is a zip, and a zip starts "PK\x03\x04". A renamed .doc or .txt does not. */
function looksLikeDocx(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/** What the bytes say the file is, whatever its name says. */
export function kindOfBytes(bytes: Uint8Array): DocumentKind | null {
  if (looksLikePdf(bytes)) return 'pdf';
  if (looksLikeDocx(bytes)) return 'docx';
  return null;
}

export async function probeFile(
  file: Readable,
  timeoutMs = readTimeoutFor(file.size),
): Promise<ProbeResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('read timed out')), timeoutMs);
  });

  try {
    const buffer = await Promise.race([file.arrayBuffer(), deadline]);
    const bytes = new Uint8Array(buffer);
    const kind = kindOfBytes(bytes);
    if (!kind) return { ok: false, reason: NOT_PDF_MESSAGE };
    return { ok: true, bytes, kind };
  } catch {
    // A NotReadableError, a NotFoundError, or our own deadline: the advice is
    // the same, because the cause is the same.
    return { ok: false, reason: UNREADABLE_MESSAGE };
  } finally {
    clearTimeout(timer);
  }
}

/** The slice a head probe needs. Real Files have it; tests can fake it. */
export interface HeadReadable {
  size: number;
  slice(start: number, end: number): { arrayBuffer(): Promise<ArrayBuffer> };
}

/**
 * probeFile for a batch: the first kilobyte only.
 *
 * A bulk queue may hold a hundred files at 25 MB each, and reading all of
 * them at selection would hold gigabytes in memory to answer a question the
 * first kilobyte answers just as well — a placeholder fails or hangs on its
 * first byte the same as on its last, and "%PDF" is in the first kilobyte or
 * nowhere. The full read happens per file, just before that file's upload.
 */
export async function probeHead(
  file: HeadReadable,
  timeoutMs = readTimeoutFor(1024),
): Promise<{ ok: true } | { ok: false; reason: string }> {
  const head = { size: 1024, arrayBuffer: () => file.slice(0, 1024).arrayBuffer() };
  const result = await probeFile(head, timeoutMs);
  return result.ok ? { ok: true } : result;
}

/** The whole file, or an Error carrying the message to show. */
export async function readFully(file: Readable): Promise<Uint8Array> {
  const result = await probeFile(file);
  if (!result.ok) throw new Error(result.reason);
  return result.bytes;
}

/**
 * How long an upload may go without moving before it is called stalled.
 *
 * With the bytes already in memory a stall can only be the network. Thirty
 * seconds with no progress event is not a slow connection; it is a dead one,
 * and the honest thing is to say so and offer a retry rather than a bar that
 * never moves.
 */
export const STALL_MS = 30_000;

export const STALLED_MESSAGE =
  'The upload stalled — nothing was sent for half a minute. Check your connection and try again.';

class UploadStalled extends Error {
  override name = 'UploadStalled';
}

/** A filename Cloud Storage will accept, keeping the extension. */
export function safeFileName(name: string): string {
  const cleaned = name
    .replace(/[^\w.\- ]+/g, '')
    .replace(/\s+/g, '-')
    .slice(-120);
  return cleaned || 'document.pdf';
}

/**
 * What the What's New feed says about a freshly uploaded policy.
 *
 * submitPolicyForReview requires a change summary of at least ten characters
 * and neither upload form asked for one, so every "Submit for approval"
 * failed with a message about a field the person had never seen. There is
 * nothing to ask them: an upload is a first version, and this says so. The
 * fixed wording alone clears the minimum, whatever the file is called.
 */
export function changeSummaryFor(fileName: string): string {
  return `Initial version, uploaded from "${fileName}".`;
}

export type StageListener = (stage: UploadStage) => void;

/**
 * Turn an upload failure into the sentence a person sees.
 *
 * A storage failure is a plain Error with a `code` like "storage/unauthorized";
 * a callable failure has already been shaped by lib/callable; a stall is ours.
 */
export function failureMessage(e: unknown): string {
  return isCallFailure(e)
    ? e.message
    : e instanceof UploadStalled
      ? STALLED_MESSAGE
      : e instanceof Error && e.message.includes('storage/unauthorized')
        ? 'You do not have permission to upload to this organization.'
        : e instanceof Error
          ? e.message
          : 'The upload failed. Please try again.';
}

/**
 * The three steps for ONE policy, reporting each as it starts.
 *
 * This is the whole pipeline, and the only copy of it: the single-file page
 * calls it once, the bulk page calls it per file. Resolves to the policy id;
 * throws on any failure, with failureMessage() turning the throw into words.
 */
export async function uploadOnePolicy(
  orgId: string,
  input: UploadInput,
  onStage: StageListener,
): Promise<string> {
    onStage({ phase: 'creating' });

    // Keys with no value are OMITTED, not set to undefined. Firebase
    // callables serialize the payload as JSON, and JSON has no undefined
    // — it becomes null on the wire, which a `z.array().optional()` on
    // the backend rejects as "Expected array, received null". Sending
    // `departmentIds: undefined` failed every upload for exactly that
    // reason, and no unit test could see it because the serialization
    // happens in the SDK.
    const draft: Record<string, unknown> = {
      title: input.title,
      reviewCycleMonths: input.reviewCycleMonths ?? 12,
      requiresAcknowledgement: input.requiresAcknowledgement,
      content: '',
    };
    if (input.summary) draft.summary = input.summary;
    if (input.categoryId) draft.categoryId = input.categoryId;
    if (input.documentTypeId) draft.documentTypeId = input.documentTypeId;
    if (input.departmentIds?.length) draft.departmentIds = input.departmentIds;
    if (input.targetAudience === 'departments' && input.departmentIds?.length) draft.targetAudience = 'departments';

    draft.clientRequestId = input.attemptId;
    const created = await call<Record<string, unknown>, { policyId: string }>(
      'savePolicyDraft',
      draft,
    );

    const policyId = created.policyId;
    const fileName = safeFileName(input.file.name);
    // `draftId` in the rules path; the current draft is version 1 of a new
    // policy, and the path only has to be stable and inside the org.
    const path = `orgs/${orgId}/policies/${policyId}/drafts/current/attachments/${fileName}`;

    onStage({ phase: 'uploading', percent: 0 });
    // The bytes, not the File. See probeFile.
    const task = uploadBytesResumable(ref(storage, path), input.bytes, {
      contentType: input.file.type,
    });

    await new Promise<void>((resolve, reject) => {
      // Reset on every progress event; fires only if none arrives.
      let watchdog: ReturnType<typeof setTimeout>;
      const stalled = () => {
        task.cancel();
        reject(new UploadStalled(STALLED_MESSAGE));
      };
      watchdog = setTimeout(stalled, STALL_MS);

      task.on(
        'state_changed',
        (snap) => {
          clearTimeout(watchdog);
          watchdog = setTimeout(stalled, STALL_MS);
          const percent = snap.totalBytes
            ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
            : 0;
          onStage({ phase: 'uploading', percent });
        },
        (err) => {
          clearTimeout(watchdog);
          reject(err);
        },
        () => {
          clearTimeout(watchdog);
          resolve();
        },
      );
    });

    /**
     * NO DOWNLOAD URL IS ASKED FOR OR SENT. `getDownloadURL` mints a token
     * that reads the object forever, for anybody holding the link, signed out.
     * Kept on the record beside the file it made the link itself the
     * permission (blocker B2). The path goes up instead, and a reader asks
     * `getPolicyFileUrl` for a URL that lives fifteen minutes.
     */
    onStage({ phase: 'attaching' });
    const kind = kindOfBytes(input.bytes) ?? documentKind(input.file) ?? 'pdf';
    // `setDraftDocument`, not `addDraftAttachment`: this file IS the document,
    // and saying so in the same write is what makes a replacement atomic (B6).
    const attached = await call<unknown, { attachmentId: string }>('setDraftDocument', {
      policyId,
      attachment: {
        name: input.file.name,
        // What the bytes are, not what the picker guessed; a .docx with an
        // empty type would otherwise be recorded as nothing in particular.
        contentType: kind === 'docx' ? DOCX_TYPE : ACCEPTED_TYPE,
        size: input.file.size,
        storagePath: path,
      },
    });

    // A Word document is kept as the original and converted to the PDF that
    // staff read and search indexes. A conversion that fails does not undo
    // the upload: the draft exists with its Word file, and the page says so.
    let note: string | undefined;
    if (kind === 'docx') {
      onStage({ phase: 'converting' });
      try {
        await call('convertDraftDocxToPdf', { policyId, attachmentId: attached.attachmentId });
      } catch (e) {
        note = `Saved as a draft with the Word file, but converting it to PDF failed: ${failureMessage(e)} Open the policy to try again.`;
      }
    }

    if (input.submitForReview && !note) {

      onStage({ phase: 'submitting' });

      // The backend refuses a submission without a change summary of at

      // least ten characters — it feeds the What's New feed. An upload is

      // always a policy's first version, so the honest summary is that.

      await call('submitPolicyForReview', {

        policyId,

        changeSummary: changeSummaryFor(input.file.name),

      });

    }

    if (note) throw new ConversionFailed(policyId, note);
    return policyId;
}

/** The upload succeeded and the draft exists; only the conversion did not. */
export class ConversionFailed extends Error {
  constructor(public readonly policyId: string, message: string) {
    super(message);
    this.name = 'ConversionFailed';
  }
}

export function useUploadPolicy(orgId: string | null) {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<UploadStage>({ phase: 'idle' });

  const reset = useCallback(() => setStage({ phase: 'idle' }), []);

  const upload = useCallback(
    async (input: UploadInput) => {
      if (!orgId) {
        setStage({ phase: 'failed', message: 'No organization is selected.' });
        return;
      }
      try {
        const policyId = await uploadOnePolicy(orgId, input, setStage);
        void queryClient.invalidateQueries({ queryKey: ['policies', orgId] });
        setStage({ phase: 'done', policyId });
      } catch (e) {
        if (e instanceof ConversionFailed) {
          void queryClient.invalidateQueries({ queryKey: ['policies', orgId] });
          setStage({ phase: 'done', policyId: e.policyId, note: e.message });
          return;
        }
        setStage({ phase: 'failed', message: failureMessage(e) });
      }
    },
    [orgId, queryClient],
  );

  return { stage, upload, reset };
}
